import { PropsWithChildren } from 'react'
import styled from 'styled-components'

import { createTestId } from 'aem-libs-tests/src/utility/createTestId'

import { Spinner, SpinnerProps } from '../spinner/Spinner'
import { useLoader } from './useLoader'

const LoaderStyled = styled.div<{ $spinnerColor: 'white' | 'blue' }>`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;

  .spinner-css {
    font-size: 1rem;
    animation: fadeIn 1s;
  }

  .spinner-css__bars {
    width: auto;
    height: 26px;
    text-align: center;
    margin: 0 auto;
  }

  .spinner-css__bars > i {
    background-color: #fff;
    background-color: ${({ $spinnerColor, theme }) =>
      $spinnerColor === 'blue' ? theme.colors.icon.active : theme.colors.background.white};
    height: 100%;
    width: 7px;
    margin-right: 0.3em;
    display: inline-block;
    animation: stretchdelay 1s infinite ease-in-out;
  }

  .spinner-css__bars > i:nth-child(1) {
    animation-delay: -0.4s;
  }

  .spinner-css__bars > i:nth-child(2) {
    animation-delay: -0.3s;
  }

  .spinner-css__bars > i:nth-child(3) {
    animation-delay: -0.2s;
  }

  .spinner-css__bars > i:nth-child(4) {
    animation-delay: -0.1s;
  }

  @keyframes stretchdelay {
    0%,
    100%,
    80% {
      transform: scaleY(0.3333);
    }

    40% {
      transform: scaleY(1);
    }
  }
`

export const LoaderWrapper = styled.div`
  position: relative;
  height: 100%;
`

export const LoaderBg = styled.div<{ $loading?: boolean }>`
  width: 100%;
  height: 100%;
  ${({ $loading }) => $loading && 'opacity: 0.2; z-index: 999;  filter: blur(5px);'}
`

const defaultDelay = 200

export interface LoaderProps {
  spinnerColor?: SpinnerProps['spinnerColor']
  loading?: boolean
  loadingDelay?: number
  testId?: string
}

export const Loader = ({
  spinnerColor = 'white',
  testId,
  loading,
  children,
  loadingDelay = defaultDelay
}: PropsWithChildren<LoaderProps>) => {
  const showLoader = useLoader(loading ?? false, loadingDelay)
  const usedTestId = testId ?? 'loader'

  return (
    <LoaderWrapper aria-busy={showLoader} data-testid={usedTestId}>
      {showLoader && <Spinner spinnerColor={spinnerColor} testId={createTestId(usedTestId, 'spinner')} />}
      <LoaderBg $loading={showLoader} data-testid={createTestId(usedTestId, 'bg')}>
        {children}
      </LoaderBg>
    </LoaderWrapper>
  )
}
